var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import orchestratorApiClient from './orchestrator-api-client';
// ---------------------------------------------------------------------------
// Export (orchestrator, JWT auth)
// ---------------------------------------------------------------------------
export function requestExport(request) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const { conversation_id, export_query_id, format } = request;
            const response = yield orchestratorApiClient.post(`/chat/${conversation_id}/export`, {
                export_query_id,
                format
            });
            return response.data;
        }
        catch (error) {
            console.error('[exportApi] Error requesting export:', error);
            throw error;
        }
    });
}
/**
 * Get the status of an async export job.
 * GET /api/chat/exports/{exportId}/status
 */
export function getExportStatus(exportId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield orchestratorApiClient.get(`/chat/exports/${exportId}/status`);
            return response.data;
        }
        catch (error) {
            console.error('[exportApi] Error fetching export status:', error);
            throw error;
        }
    });
}
// ---------------------------------------------------------------------------
// Exponential backoff polling
// ---------------------------------------------------------------------------
const POLL_INITIAL_DELAY_MS = 2000;
const POLL_MULTIPLIER = 1.5;
const POLL_MAX_DELAY_MS = 10000;
const POLL_MAX_ATTEMPTS = 60;
function wait(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
            reject(new Error('Cancelled'));
            return;
        }
        const timeout = setTimeout(resolve, ms);
        signal === null || signal === void 0 ? void 0 : signal.addEventListener('abort', () => {
            clearTimeout(timeout);
            reject(new Error('Cancelled'));
        });
    });
}
/**
 * Poll an export job until completion with exponential backoff.
 * Returns the final download URL or throws on failure/timeout.
 */
export function pollExportUntilComplete(exportId, options) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        let delay = POLL_INITIAL_DELAY_MS;
        for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
            // Check if aborted before waiting
            if ((_a = options === null || options === void 0 ? void 0 : options.signal) === null || _a === void 0 ? void 0 : _a.aborted) {
                throw new Error('Export polling cancelled');
            }
            // eslint-disable-next-line no-await-in-loop
            yield wait(delay, options === null || options === void 0 ? void 0 : options.signal);
            // Check again after wait
            if ((_b = options === null || options === void 0 ? void 0 : options.signal) === null || _b === void 0 ? void 0 : _b.aborted) {
                throw new Error('Export polling cancelled');
            }
            // eslint-disable-next-line no-await-in-loop
            const status = yield getExportStatus(exportId);
            if (status.progress !== undefined && (options === null || options === void 0 ? void 0 : options.onProgress)) {
                options.onProgress(status.progress);
            }
            if (status.status === 'complete') {
                if (!status.download_url) {
                    throw new Error('Export completed without a download URL');
                }
                return status;
            }
            if (status.status === 'failed') {
                throw new Error(status.error_message || 'Export failed');
            }
            // Exponential backoff
            delay = Math.min(delay * POLL_MULTIPLIER, POLL_MAX_DELAY_MS);
        }
        throw new Error('Export polling timed out');
    });
}
/**
 * Triggers a browser download from a presigned URL
 */
export function downloadFromUrl(url, fileName) {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
